import { TableScroll } from '@web/components/table-scroll.tsx';
import { useReportPaging } from '@web/components/report-pagination.tsx';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { EmptyState, classes } from '@web/components/ui.tsx';
import { AGING_RAMP, BarListChart, CHART_SERIES, type BarListRow } from '@web/components/reports/charts.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '@web/i18n/locale.ts';
import {
  reportRetry,
  useReportDate,
  StatTile,
  ReportBoundary,
  SectionCard,
  CustomerLink,
  TH,
  THR,
  TD,
  TDR,
  ROW,
} from '../shared.tsx';

export function ReceivablesSection({ scope }: { scope?: 'all' }) {
  const formatDate = useReportDate();
  const { t } = useTranslation();
  const paging = useReportPaging({ scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'receivables', { scope, pages }, paging.identity],
    queryFn: () => reportsApi.receivables(scope, pages),
    retry: reportRetry,
  });

  return (
    <ReportBoundary query={query} paging={paging}>
      {(report) => {
        const money = (cents: number) => formatCents(cents, report.currencyCode, getDateLocale());
        const failedTotal =
          report.failedSummary?.totalCents ?? report.failed.reduce((sum, row) => sum + row.outstandingCents, 0);
        const agingRows: BarListRow[] = report.aging.map((bucket, index) => ({
          label: t(`reports.receivables.aging.${bucket.bucket}`),
          value: bucket.totalCents,
          display: money(bucket.totalCents),
          color: AGING_RAMP[index] ?? AGING_RAMP[AGING_RAMP.length - 1] ?? CHART_SERIES[0],
          note: t('reports.receivables.visitCount', { count: bucket.visits }),
        }));
        const methodRows: BarListRow[] = report.methodMix30d.map((entry) => ({
          label: t(`reports.receivables.method.${entry.method}`),
          value: entry.amountCents,
          display: money(entry.amountCents),
          color: CHART_SERIES[0],
        }));

        if (report.outstanding.visits === 0 && report.openItems.length === 0) {
          return (
            <EmptyState title={t('reports.receivables.emptyTitle')}>{t('reports.receivables.emptyBody')}</EmptyState>
          );
        }

        return (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-3">
              <StatTile
                label={t('reports.receivables.outstanding')}
                value={money(report.outstanding.totalCents)}
                hint={t('reports.receivables.outstandingHint', { count: report.outstanding.visits })}
              />
              <StatTile
                label={t('reports.receivables.failed')}
                value={String(report.failedSummary?.visits ?? report.failed.length)}
                hint={t('reports.receivables.failedHint', { value: money(failedTotal) })}
              />
              <StatTile
                label={t('reports.receivables.daysToPay')}
                value={
                  report.daysToPay.avgDays === null
                    ? '—'
                    : t('reports.receivables.daysValue', { count: Math.round(report.daysToPay.avgDays) })
                }
                hint={
                  report.daysToPay.medianDays === null
                    ? t('reports.receivables.daysEmptyHint')
                    : t('reports.receivables.daysHint', {
                        median: Math.round(report.daysToPay.medianDays),
                        count: report.daysToPay.paidVisits,
                      })
                }
              />
            </div>

            <div className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-2">
                <SectionCard
                  title={t('reports.receivables.agingTitle')}
                  hint={t('reports.receivables.agingHint')}
                  csvHref={reportsApi.csvUrl('receivables', { scope })}
                >
                  <BarListChart rows={agingRows} ariaLabel={t('reports.receivables.agingTitle')} />
                </SectionCard>
                <SectionCard title={t('reports.receivables.methodTitle')} hint={t('reports.receivables.methodHint')}>
                  {methodRows.length === 0 ? (
                    <p className="text-[color:var(--color-muted)]">{t('reports.receivables.methodEmpty')}</p>
                  ) : (
                    <BarListChart rows={methodRows} ariaLabel={t('reports.receivables.methodTitle')} />
                  )}
                </SectionCard>
              </div>

              {report.topDebtors.length > 0 || (report.pagination?.topDebtors?.page ?? 1) > 1 ? (
                <SectionCard
                  title={t('reports.receivables.debtorsTitle')}
                  collection="topDebtors"
                  hint={t('reports.receivables.debtorsHint')}
                >
                  <TableScroll aria-label={t('reports.receivables.debtorsTitle')} className="overflow-x-auto">
                    <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                      <thead>
                        <tr className="border-b border-[color:var(--color-line)]">
                          <th scope="col" className={TH}>
                            {t('reports.receivables.colCustomer')}
                          </th>
                          <th scope="col" className={THR}>
                            {t('reports.receivables.colVisits')}
                          </th>
                          <th scope="col" className={THR}>
                            {t('reports.receivables.colOutstanding')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.topDebtors.map((row) => (
                          <tr key={row.customerId} className={ROW}>
                            <td className={TD}>
                              <CustomerLink id={row.customerId} name={row.customerName} />
                            </td>
                            <td className={TDR}>{row.visits}</td>
                            <td className={TDR}>{money(row.outstandingCents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                </SectionCard>
              ) : null}

              <SectionCard
                title={t('reports.receivables.openItemsTitle')}
                collection="openItems"
                hint={t('reports.receivables.openItemsHint')}
              >
                {report.openItems.length === 0 ? (
                  <p className="text-[color:var(--color-muted)]">{t('reports.receivables.openItemsEmpty')}</p>
                ) : (
                  <>
                    <TableScroll aria-label={t('reports.receivables.openItemsTitle')} className="overflow-x-auto">
                      <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                        <thead>
                          <tr className="border-b border-[color:var(--color-line)]">
                            <th scope="col" className={TH}>
                              {t('reports.receivables.colCustomer')}
                            </th>
                            <th scope="col" className={THR}>
                              {t('reports.receivables.colVisitDate')}
                            </th>
                            <th scope="col" className={TH}>
                              {t('reports.receivables.colStatus')}
                            </th>
                            <th scope="col" className={THR}>
                              {t('reports.receivables.colPrice')}
                            </th>
                            <th scope="col" className={THR}>
                              {t('reports.receivables.colOutstanding')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.openItems.map((row) => (
                            <tr key={row.appointmentId} className={ROW}>
                              <td className={TD}>
                                <CustomerLink id={row.customerId} name={row.customerName} />
                              </td>
                              <td className={TDR}>{formatDate(row.scheduledDate)}</td>
                              <td className={classes(TD, 'pl-3')}>
                                {t(`payments.appointmentStatus.${row.paymentStatus}`)}
                              </td>
                              <td className={TDR}>{money(row.priceCents)}</td>
                              <td className={TDR}>{money(row.outstandingCents)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableScroll>
                  </>
                )}
              </SectionCard>
            </div>
          </>
        );
      }}
    </ReportBoundary>
  );
}
